/**
=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

* This template has been built upon, with additional code and configuration by Tazi Rides Ltd.
*/

import { useEffect, useMemo, useState } from 'react'

import Card from '@mui/material/Card'
import { DateRangePickerComponent, RangeEventArgs } from '@syncfusion/ej2-react-calendars'
// import { useQuery } from '@tanstack/react-query'
import { useInfiniteQuery } from '@tanstack/react-query'
import axios from 'axios'

import MDBox from 'components/MDBox'
import MDTypography from 'components/MDTypography'
import { useMaterialUIController } from 'context'
import DashboardLayout from 'modules/LayoutContainers/DashboardLayout'
import LoadingComponent from 'modules/Loading'
import DashboardNavbar from 'modules/Navbars/DashboardNavbar'
import DataTable from 'modules/Tables/DataTable'
import { ROUTE_PATHS } from 'routes'
import { FilterListValue, TabularData } from 'types/server/tabular'

function CallDataOverview(): JSX.Element {
	const [controller] = useMaterialUIController()
	const [currentPageIndex, setCurrentPageIndex] = useState<number>(0)

	const { session } = controller

	const initEnd = new Date()
	initEnd.setHours(23, 59, 59)
	const initStart = new Date(initEnd)
	initStart.setDate(initEnd.getDate() - 6)
	initStart.setHours(0, 0, 0)
	const PAGINATION_LIMIT = 25

	const [selectedDateRange, setSelectedDateRange] = useState<{ start: Date; end: Date }>({
		start: initStart,
		end: initEnd,
	})

	const [searchText, setSearchText] = useState<string>('')
	const [debouncedSearch, setDebouncedSearch] = useState<string>('')
	const [filterValues, setFilterValues] = useState<Record<string, string | null>>({})

	useEffect(() => {
		const handle = window.setTimeout(() => {
			setDebouncedSearch(searchText.trim())
		}, 600)
		return () => window.clearTimeout(handle)
	}, [searchText])

	const stableFilterKey = useMemo(() => {
		const entries = Object.entries(filterValues)
			.filter(([, v]) => v)
			.sort(([a], [b]) => a.localeCompare(b))
		return JSON.stringify(entries)
	}, [filterValues])

	const fetchData = async ({ pageParam = 0 }) => {
		const activeFilters = Object.fromEntries(
			Object.entries(filterValues).filter(([, v]) => v !== null && v !== '')
		)
		const response = await axios.get<TabularData>(`${process.env.REACT_APP_API_URL}/call/table`, {
			params: {
				page: pageParam,
				limit: PAGINATION_LIMIT,
				start: selectedDateRange.start.getTime(),
				end: selectedDateRange.end.getTime(),
				...(debouncedSearch ? { search: debouncedSearch } : {}),
				...activeFilters,
			},
			headers: {
				Authorization: await session.user.getIdToken(),
			},
		})
		return response.data
	}

	const {
		data,
		error,
		isLoading,
		isFetchingNextPage,
		isFetchingPreviousPage,
		fetchNextPage,
		hasNextPage,
	} = useInfiniteQuery({
		queryKey: [
			'call-table',
			selectedDateRange.start.getTime(),
			selectedDateRange.end.getTime(),
			debouncedSearch,
			stableFilterKey,
		],
		queryFn: fetchData,
		getNextPageParam: (lastPage, _pages) => lastPage.next,
		enabled: !!selectedDateRange.start && !!selectedDateRange.end,
		refetchOnWindowFocus: false,
	})

	useEffect(() => {
		setCurrentPageIndex(0)
	}, [selectedDateRange])

	useEffect(() => {
		setCurrentPageIndex(0)
	}, [debouncedSearch, stableFilterKey])

	const aggregatedFilters = useMemo<FilterListValue | undefined>(() => {
		if (!data?.pages) return undefined
		const allFilters = data.pages.map(page => page.filters).filter(Boolean)
		if (allFilters.length === 0) return undefined

		const fields = allFilters[0].fields
		const options: Record<string, string[]> = {}
		for (const field of fields) {
			const merged = new Set<string>()
			for (const filter of allFilters) {
				filter.options[field]?.forEach(val => merged.add(val))
			}
			options[field] = [...merged].sort()
		}
		return { fields, options }
	}, [data])

	useEffect(() => {
		if (!aggregatedFilters) return
		setFilterValues(prev => {
			const next: Record<string, string | null> = {}
			for (const field of aggregatedFilters.fields) {
				next[field] = field in prev ? prev[field] : null
			}
			return next
		})
	}, [aggregatedFilters])

	const today = new Date()
	const DATE_RANGE_PRESETS = [
		{
			label: 'Today',
			start: today,
			end: today,
		},
		{
			label: 'Last 7 Days',
			start: new Date(today.getFullYear(), today.getMonth(), today.getDate() - 6),
			end: today,
		},
		{
			label: 'Last 14 Days',
			start: new Date(today.getFullYear(), today.getMonth(), today.getDate() - 13),
			end: today,
		},
		{
			label: 'Last 30 Days',
			start: new Date(today.getFullYear(), today.getMonth(), today.getDate() - 29),
			end: today,
		},
	]

	const handleDateChange = (args: RangeEventArgs) => {
		if (!args.value) {
			console.log('No value provided')
			return
		}

		if (Array.isArray(args.value)) {
			// Handle the case when value is Date[]
			console.log('Received an array of dates:', args.value)
			const newStart = new Date(args.value[0])
			const newEnd = new Date(args.value[1])
			newStart.setHours(0, 0, 0)
			newEnd.setHours(23, 59, 59)
			setSelectedDateRange({ start: newStart, end: newEnd })
		} else {
			// Handle the case when value is DateRange
			console.log('Received an array of dates:', args.value)
			const newStart = new Date(args.value.start)
			const newEnd = new Date(args.value.end)
			newStart.setHours(0, 0, 0)
			newEnd.setHours(23, 59, 59)
			setSelectedDateRange({ start: newStart, end: newEnd })
		}
	}

	return (
		<DashboardLayout>
			<DashboardNavbar />
			<MDBox pt={6} pb={3}>
				<Card>
					<MDBox p={3} lineHeight={1}>
						<MDTypography variant="h2" fontWeight="medium">
							Call Data
						</MDTypography>
						<MDTypography variant="button" color="text">
							This is granular data that provides detailed insights and allows for comprehensive
							analysis.
						</MDTypography>
					</MDBox>
					<MDBox display={'flex'} flexDirection={'row'} justifyContent={'flex-end'} gap={2} mr={3}>
						{/* <MDTypography variant="body2">Select Dates</MDTypography> */}
						<DateRangePickerComponent
							id="daterangepicker"
							format="dd MMMM yyyy"
							firstDayOfWeek={1}
							cssClass="custom-date-picker"
							placeholder="Enter Date Range"
							startDate={selectedDateRange.start}
							endDate={selectedDateRange.end}
							allowEdit={false}
							showClearButton={false}
							showTodayButton={true}
							max={new Date()}
							maxDays={31}
							width={'20rem'}
							presets={DATE_RANGE_PRESETS}
							onChange={handleDateChange}
						/>
					</MDBox>
					{!isLoading && !isFetchingNextPage && !isFetchingPreviousPage && !error ? (
						<DataTable
							table={{
								columns: data.pages[data.pages.length - 1].table.columns,
								rows: data?.pages?.[currentPageIndex]?.table?.rows ?? [],
							}}
							canSearch={true}
							remoteSearch={{
								value: searchText,
								onChange: setSearchText,
							}}
							filters={aggregatedFilters}
							remoteFilters={{
								values: filterValues,
								onChange: (field, value) => {
									setFilterValues(prev => ({ ...prev, [field]: value }))
								},
							}}
							parentPath={ROUTE_PATHS.CALLS.BASE}
							entriesPerPage={{ defaultValue: PAGINATION_LIMIT, entries: [PAGINATION_LIMIT] }}
							pagination={{
								type: 'remote',
								variant: 'contained',
								color: 'primary',
								remotePagination: {
									fetchNextPage,
									hasNextPage,
									currentPageIndex,
									pageIndexes: data.pages.map((_, index) => index),
									setCurrentPageIndex,
								},
							}}
						/>
					) : (
						<LoadingComponent type="box" height={600} containerStyle={{ padding: 50 }} />
					)}
				</Card>
			</MDBox>
		</DashboardLayout>
	)
}

export default CallDataOverview
